export interface PostingEquipmentOverrides {
  atgOverride: string | null;
  datOverride: string | null;
  tsOverride: string | null;
  tsSubcodeOverride: string | null;
  mailerOverride: string | null;
}

/**
 * Maps a board (available service) name to its primary override field on the
 * posting payload. Matched case-insensitively so minor API casing variants
 * (e.g. "Truckstop" vs "TruckStop") still resolve.
 */
const BOARD_OVERRIDE_FIELD: Partial<
  Record<string, keyof PostingEquipmentOverrides>
> = {
  atg: 'atgOverride',
  dat: 'datOverride',
  truckstop: 'tsOverride',
  mailer: 'mailerOverride',
};

/**
 * Maps a board name to its secondary "subcode" override field. Only Truckstop
 * exposes a subcode alongside its primary code; all other boards have a single
 * code, so they are absent from this map.
 */
const BOARD_SUBCODE_FIELD: Partial<
  Record<string, keyof PostingEquipmentOverrides>
> = {
  truckstop: 'tsSubcodeOverride',
};

/**
 * Builds the equipment-code override payload sent when posting a load.
 *
 * Only boards the user manually edited AND currently has selected contribute a
 * value; everything else stays null ("edited value only, else null"). The
 * optional `editedSubcodes` map carries the Truckstop subcode the same way.
 */
export const buildEquipmentOverridePayload = (
  editedCodes: Record<string, string | undefined>,
  selectedBoards: string[],
  editedSubcodes: Record<string, string | undefined> = {},
): PostingEquipmentOverrides => {
  const overrides: PostingEquipmentOverrides = {
    atgOverride: null,
    datOverride: null,
    tsOverride: null,
    tsSubcodeOverride: null,
    mailerOverride: null,
  };
  const selected = new Set(selectedBoards);

  Object.entries(editedCodes).forEach(([boardName, code]) => {
    const field = BOARD_OVERRIDE_FIELD[boardName.trim().toLowerCase()];
    const value = code?.trim();
    if (field && value && selected.has(boardName)) {
      overrides[field] = value;
    }
  });

  Object.entries(editedSubcodes).forEach(([boardName, subcode]) => {
    const field = BOARD_SUBCODE_FIELD[boardName.trim().toLowerCase()];
    const value = subcode?.trim();
    if (field && value && selected.has(boardName)) {
      overrides[field] = value;
    }
  });

  return overrides;
};

/**
 * Rebuilds the in-form `editedCodes` map from override fields returned by the
 * posting GET/POST/PUT responses, so a previously saved manual override is
 * shown (and resubmitted) instead of being overwritten by a freshly fetched
 * `posting/equipmenttypecode` value when an existing posting is reopened.
 *
 * Keyed by the caller's board (service) names so it lines up with the rendered
 * board rows; only boards with a non-empty saved override contribute an entry.
 */
export const hydrateEquipmentOverrides = (
  source: Partial<PostingEquipmentOverrides> | null | undefined,
  boardNames: string[],
): Record<string, string> => {
  const editedCodes: Record<string, string> = {};
  if (!source) {
    return editedCodes;
  }

  boardNames.forEach((boardName) => {
    const field = BOARD_OVERRIDE_FIELD[boardName.trim().toLowerCase()];
    const value = field ? source[field]?.trim() : undefined;
    if (value) {
      editedCodes[boardName] = value;
    }
  });

  return editedCodes;
};

/**
 * Rebuilds the in-form `editedSubcodes` map (board name -> saved subcode) from
 * the subcode override fields returned on a reopened posting. Mirrors
 * {@link hydrateEquipmentOverrides} but only the boards that expose a subcode
 * (Truckstop) can contribute an entry.
 */
export const hydrateEquipmentSubcodeOverrides = (
  source: Partial<PostingEquipmentOverrides> | null | undefined,
  boardNames: string[],
): Record<string, string> => {
  const editedSubcodes: Record<string, string> = {};
  if (!source) {
    return editedSubcodes;
  }

  boardNames.forEach((boardName) => {
    const field = BOARD_SUBCODE_FIELD[boardName.trim().toLowerCase()];
    const value = field ? source[field]?.trim() : undefined;
    if (value) {
      editedSubcodes[boardName] = value;
    }
  });

  return editedSubcodes;
};

/**
 * Whether a board exposes a secondary subcode field (only Truckstop does).
 * Used by the UI to decide whether to render the second editable text field.
 */
export const boardHasSubcode = (boardName: string): boolean =>
  Boolean(BOARD_SUBCODE_FIELD[boardName.trim().toLowerCase()]);
